/**
 * Cadeia de processamento ponta a ponta.
 *
 * 🛑 **A tela distingue dois tipos de correlação, e isso é requisito.** Cliente
 * e motor correlacionam por identificador de rastreio propagado em cabeçalho.
 * Acelerador e sistema correlacionam apenas por **janela temporal** — atribuir
 * uma rotina de acelerador a uma requisição específica exigiria instrumentação
 * de perfilamento fora do escopo.
 *
 * Apresentar correlação temporal com a mesma aparência de rastreamento
 * distribuído seria afirmar uma precisão que o sistema não tem.
 */

import { useMemo } from "react";
import { Badge, Tooltip } from "@cloudflare/kumo";
import { motion } from "motion/react";
import { DataTable, type Column } from "../components/DataTable.js";
import { Notice } from "../components/Notice.js";
import { Page } from "../components/Page.js";
import { Panel } from "../components/Panel.js";
import { snapshot } from "../data/source.js";
import { formatNumber } from "../lib/format.js";

/** Cenário cuja cadeia é decomposta nesta tela. */
const PIPELINE_SCENARIO_ID = 3;

/** Como um elo da cadeia se liga aos demais. */
type Correlation = "trace" | "window";

/** Um elo da cadeia, com o tempo que consome e como foi correlacionado. */
interface Hop {
  readonly key: string;
  readonly stage: string;
  readonly source: string;
  readonly ms: number;
  readonly correlation: Correlation;
  readonly note: string;
}

/** Explicação de cada tipo de correlação, exibida junto do selo. */
const CORRELATION_HINT: Readonly<Record<Correlation, string>> = {
  trace: "Ligado por identificador de rastreio propagado em cabeçalho: este tempo pertence a ESTA requisição.",
  window:
    "Ligado por janela temporal: este tempo pertence ao intervalo em que a requisição ocorreu, e não à requisição em si. Atribuir uma rotina de acelerador a uma requisição exigiria perfilamento fora do escopo.",
};

/**
 * Aparência do selo e do marcador de correlação.
 *
 * Rastreio sai na cor da marca, cheio; janela temporal sai neutra. A diferença de
 * peso visual é a própria mensagem: um é preciso, o outro é aproximado.
 */
const CORRELATION_STYLE: Readonly<Record<Correlation, { variant: "info" | "secondary"; label: string; dot: string }>> = {
  trace: { variant: "info", label: "por identificador", dot: "bg-kumo-brand" },
  window: { variant: "secondary", label: "por janela temporal", dot: "bg-kumo-line" },
};

/** Selo de correlação, com a explicação ao pousar o ponteiro. */
function CorrelationBadge({ correlation }: { readonly correlation: Correlation }): React.ReactElement {
  return (
    <Tooltip content={CORRELATION_HINT[correlation]} render={<span className="inline-flex" />}>
      <Badge variant={CORRELATION_STYLE[correlation].variant}>{CORRELATION_STYLE[correlation].label}</Badge>
    </Tooltip>
  );
}

/**
 * Parcela do tempo até o 1º token atribuída a cada elo.
 *
 * Repartição do conjunto sintético, calibrada pelo que se observa em SGLang com cache de
 * prefixo: o processamento inicial domina; fila, transferência, rede e cliente dividem o
 * resto. Soma 1,0.
 */
const HOP_SHARE = { client: 0.06, network: 0.08, queue: 0.14, prefill: 0.63, transfer: 0.09 } as const;

/** Decomposição da cadeia, elo a elo, com a origem de cada medida. */
export function Pipeline(): React.ReactElement {
  const stats = snapshot.stats.find((s) => s.scenarioId === PIPELINE_SCENARIO_ID);
  const engine = useMemo(() => snapshot.engineMetrics.filter((m) => m.scenarioId === PIPELINE_SCENARIO_ID), []);

  /** Reparte o tempo até o primeiro token entre os elos que o compõem. */
  const hops = useMemo<Hop[]>(() => {
    const ttft = stats?.ttftP95Ms ?? 0;
    const lastEngine = engine[engine.length - 1];
    return [
      {
        key: "client",
        stage: "gerador de carga",
        source: "medição de cliente",
        ms: ttft * HOP_SHARE.client,
        correlation: "trace",
        note: "montagem do prompt e envio, com relógio monotônico",
      },
      {
        key: "network",
        stage: "rede",
        source: "medição de cliente",
        ms: ttft * HOP_SHARE.network,
        correlation: "trace",
        note: "ida e volta até o frontend do orquestrador",
      },
      {
        key: "queue",
        stage: "fila do escalonador",
        source: "métricas do motor",
        ms: ttft * HOP_SHARE.queue,
        correlation: "trace",
        note: `fila de ${lastEngine?.queueSize ?? 0} requisições no instante`,
      },
      {
        key: "prefill",
        stage: "processamento inicial",
        source: "métricas do motor",
        ms: ttft * HOP_SHARE.prefill,
        correlation: "trace",
        note: `acerto de prefixo de ${formatNumber((lastEngine?.prefixHitRate ?? 0) * 100)}% reduz este elo`,
      },
      {
        key: "transfer",
        stage: "transferência de estado",
        source: "telemetria de acelerador",
        ms: ttft * HOP_SHARE.transfer,
        correlation: "window",
        note: "entre os pools de processamento inicial e de geração",
      },
    ];
  }, [stats, engine]);

  const totalMs = hops.reduce((sum, h) => sum + h.ms, 0);

  /** Colunas da tabela de elos; a parcela depende do total. */
  const columns: readonly Column<Hop>[] = [
    { key: "stage", title: "elo", render: (row) => <span className="font-medium">{row.stage}</span> },
    { key: "source", title: "origem da medida", render: (row) => row.source },
    {
      key: "ms",
      title: "tempo",
      align: "right",
      render: (row) => (
        <span className="whitespace-nowrap font-semibold text-kumo-strong">
          {formatNumber(row.ms)} ms{" "}
          <span className="text-xs font-normal text-kumo-subtle">
            ({formatNumber(totalMs > 0 ? (row.ms / totalMs) * 100 : 0)}%)
          </span>
        </span>
      ),
    },
    { key: "correlation", title: "correlação", render: (row) => <CorrelationBadge correlation={row.correlation} /> },
  ];

  return (
    <Page>
      <Notice
        tone="info"
        title="Dois tipos de correlação, e eles não são equivalentes"
        description={
          <span>
            Elos marcados <Badge variant="info">por identificador</Badge> pertencem a esta requisição. Elos marcados{" "}
            <Badge variant="secondary">por janela temporal</Badge> pertencem ao intervalo em que ela ocorreu — a
            atribuição é aproximada, e apresentá-los como iguais afirmaria uma precisão que o sistema não tem.
          </span>
        }
      />

      <Panel
        title={`Cadeia até o primeiro token — percentil 95 de ${formatNumber(totalMs)} ms`}
        bodyClassName="grid gap-8 lg:grid-cols-[11fr_13fr]"
      >
        <ol className="relative ml-1.5 flex flex-col gap-6 border-l border-kumo-line pl-6">
          {hops.map((hop, index) => {
            const sharePct = totalMs > 0 ? (hop.ms / totalMs) * 100 : 0;
            return (
              <motion.li
                key={hop.key}
                className="relative flex flex-col gap-1.5"
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.1 + index * 0.08, duration: 0.35 }}
              >
                {/* Marcador centrado na linha: 24px de recuo + metade dos 12px do ponto. */}
                <span
                  className={`absolute top-1.5 -left-[31px] size-3 rounded-full ring-4 ring-kumo-base ${CORRELATION_STYLE[hop.correlation].dot}`}
                />
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-semibold text-kumo-strong">{hop.stage}</span>
                  <span className="tabular-nums text-kumo-default">{formatNumber(hop.ms)} ms</span>
                  <CorrelationBadge correlation={hop.correlation} />
                </div>
                <span className="text-sm text-kumo-subtle">{hop.note}</span>
                <div className="h-1.5 w-full overflow-hidden rounded-full bg-kumo-recessed">
                  <motion.div
                    className={`h-full rounded-full ${CORRELATION_STYLE[hop.correlation].dot}`}
                    initial={{ width: 0 }}
                    animate={{ width: `${sharePct}%` }}
                    transition={{ delay: 0.2 + index * 0.08, duration: 0.7, ease: "easeOut" }}
                  />
                </div>
              </motion.li>
            );
          })}
        </ol>
        <DataTable columns={columns} rows={hops} rowKey={(row) => row.key} />
      </Panel>

      <Panel title="O que esta decomposição não alcança" bodyClassName="flex flex-col gap-4">
        <p className="text-sm text-kumo-default">
          Dentro do acelerador, o tempo se distribui entre rotinas de cálculo e acessos à memória. Essa repartição
          exigiria instrumentação de perfilamento, que só existe no modo de diagnóstico e cujo custo é medido à parte —
          nunca na execução que produz o número oficial.
        </p>
        <h3 className="text-sm font-semibold text-kumo-strong">Por que isso importa para a decisão de compra</h3>
        <p className="text-sm text-kumo-subtle">
          A eficiência de banda que alimenta a recomendação é derivada do próprio resultado — bytes por passo
          multiplicados por passos por segundo — e não do perfilamento. O campo de atividade de memória serve apenas
          como verificação de sanidade: ele mede ciclos ativos, não bytes úteis, sendo limite superior e não medida de
          eficiência.
        </p>
      </Panel>
    </Page>
  );
}
